import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import { messagingPageSchema } from '../components/StructuredData';
import './Features.css';

const Messaging: React.FC = () => {
  return (
    <div className="feature-page">
      <SEO
        title="Guest Messaging - TableChat | NFC-Powered Table-Side Chat for Restaurants"
        description="TableChat lets restaurant guests tap an NFC tag to instantly message staff from their phone — no app download, no QR code, no waiting. Requests for refills, the check, extra napkins, or menu questions reach your team in real time. Enhance service, drive guest engagement, and support your staff during every shift."
        canonical="https://tablechat.io/features/messaging"
      />
      <StructuredData data={messagingPageSchema} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Features', url: 'https://tablechat.io/features/messaging' },
        { name: 'Guest Messaging', url: 'https://tablechat.io/features/messaging' },
      ])} />

      {/* Hero */}
      <section className="feature-hero feature-hero-split">
        <div className="container">
          <div className="feature-hero-grid">
            <div className="feature-hero-text">
              <h1>Your Guests Shouldn't Have to Wait to Be Heard</h1>
              <p className="hero-lead">
                TableChat gives every guest a direct line to your staff. One tap on an NFC tag opens a live 
                chat on their phone — no app to download, no QR code to scan, no account to create. Refill 
                requests, menu questions, the check — everything reaches your team instantly while the guest 
                stays seated and comfortable.
              </p>
            </div>
            <div className="feature-hero-image">
              <img 
                src="/mainpageserver.png" 
                alt="Server delivering food to a smiling guest — TableChat enables real-time messaging between guests and staff" 
                className="feature-hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Messaging Flow */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>Real-Time Guest Engagement at Every Table</h2>
            <p>
              The moments between when a guest needs something and when a server notices are where service 
              breaks down. TableChat closes that gap with instant, frictionless communication that enhances 
              the experience for guests and staff alike.
            </p>
          </div>

          <div className="feature-blocks">
            {/* Tap-to-Chat */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Tap to Connect — Instantly</h3>
                <p>
                  Each table has an NFC tag. When a guest taps it with their phone, a web-based chat opens 
                  immediately in their browser. No app store, no download screen, no login form. The guest 
                  is in a live conversation within seconds — identified by table number, ready to communicate.
                </p>
                <p>
                  This works on every modern iPhone and Android device. The technology is the same NFC 
                  built into contactless payments — familiar, reliable, and already in your guests' pockets. 
                  Guests of all ages and technical comfort levels can use it without instruction.
                </p>
              </div>
              <div className="feature-visual">
                <h4>How Tap-to-Chat Works</h4>
                <ul>
                  <li>Guest taps phone to the NFC tag at their table</li>
                  <li>Web-based chat opens instantly in the phone's browser</li>
                  <li>No app download, no QR scan, no account creation</li>
                  <li>Works on iPhone (iOS 13+) and all modern Android devices</li>
                  <li>Guest is identified by table number automatically</li>
                  <li>Multiple guests at the same table can chat simultaneously</li>
                </ul>
              </div>
            </div>

            {/* AI Chat Assistant */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>AI-Powered Chat That Understands Guest Intent</h3>
                <p>
                  When a guest types "can we get more water" or "what's in the Caesar salad," TableChat's 
                  AI assistant understands the request and responds naturally. It acknowledges the guest, 
                  categorizes the request, and routes it to your staff kiosk — all within seconds.
                </p>
                <p>
                  The AI doesn't replace your staff — it supports them. It handles the initial response so 
                  your team knows exactly what's needed before they approach the table. Guests feel heard 
                  immediately, and servers can prioritize and batch requests efficiently during rush periods.
                </p>
              </div>
              <div className="feature-visual">
                <h4>What the AI Handles</h4>
                <ul>
                  <li>Instant acknowledgment so guests know their message was received</li>
                  <li>Request categorization (refill, check, question, issue)</li>
                  <li>Natural language understanding — guests type however they want</li>
                  <li>Contextual responses using your restaurant's information</li>
                  <li>Seamless handoff to staff for fulfillment</li>
                </ul>
              </div>
            </div>

            {/* Staff Kiosk */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Staff See Everything — Organized by Table</h3>
                <p>
                  Every guest message appears on your staff kiosk tablet in real time. Requests are organized 
                  by table number so your team can see at a glance which tables need attention and what they 
                  need. Tap a request to acknowledge it, swipe to clear it when fulfilled.
                </p>
                <p>
                  During a busy Friday night, this changes the dynamic. Instead of servers constantly scanning 
                  the floor trying to read body language, they have a clear queue of specific requests. 
                  Nothing falls through the cracks. No guest sits waiting while their server is tied up at 
                  another table.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Staff Kiosk Features</h4>
                <ul>
                  <li>Real-time message feed organized by table number</li>
                  <li>One-tap acknowledgment and swipe-to-clear workflow</li>
                  <li>Visual indicators for new, acknowledged, and completed requests</li>
                  <li>No training required — staff are productive within minutes</li>
                  <li>Works on standard Android tablets provided with your subscription</li>
                </ul>
              </div>
            </div>

            {/* Multi-Language */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>Every Guest, Every Language</h3>
                <p>
                  When a guest types in Spanish, French, Mandarin, or any other language, TableChat 
                  automatically translates the message for your staff. Responses go back in the guest's 
                  language. There's no language selector, no awkward setup — it just works.
                </p>
                <p>
                  For restaurants in tourist areas, diverse neighborhoods, or international hotel dining rooms, 
                  this removes one of the biggest barriers to great service. A guest who doesn't speak English 
                  can communicate just as easily as one who does — and your staff never has to struggle 
                  through a language gap.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Translation Capabilities</h4>
                <ul>
                  <li>Automatic language detection — no guest configuration</li>
                  <li>Messages translated in both directions seamlessly</li>
                  <li>Supports all major world languages</li>
                  <li>Staff always see messages in English (or their configured language)</li>
                  <li>Guests always see responses in their own language</li>
                </ul>
              </div>
            </div>

            {/* Service Enhancement */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Service Enhancement, Not Replacement</h3>
                <p>
                  TableChat is not a QR code ordering system. It doesn't replace your servers with a screen. 
                  It gives your staff a tool that makes them better at what they do — responding to guests 
                  quickly, handling more tables smoothly, and ensuring no request goes unnoticed.
                </p>
                <p>
                  Guests still interact with your team. They still get the personal, human hospitality that 
                  keeps them coming back. TableChat simply makes sure the moments between those personal 
                  interactions don't become moments of frustration — waiting for a refill, trying to flag 
                  down a server, or giving up on ordering dessert because nobody came by.
                </p>
              </div>
              <div className="feature-visual">
                <h4>The Service Difference</h4>
                <ul>
                  <li>Guests never wait silently — they can always communicate</li>
                  <li>Servers receive specific requests instead of guessing</li>
                  <li>Rush periods run smoother with a clear request queue</li>
                  <li>Guest satisfaction improves when response times drop</li>
                  <li>Staff can cover more tables without sacrificing quality</li>
                </ul>
              </div>
            </div>

            {/* Gateway to More */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>The Gateway to Guest Promotions and Loyalty</h3>
                <p>
                  Guest messaging is also the channel through which TableChat delivers <Link to="/features/promotions" className="feature-inline-link">automated promotions</Link>, 
                  daily specials, and <Link to="/features/loyalty" className="feature-inline-link">VIP loyalty enrollment</Link>. When a guest 
                  taps in, they don't just get a chat — they get a welcome message, today's specials, and an 
                  invitation to join your VIP list.
                </p>
                <p>
                  Because the guest is already in the conversation, these marketing touchpoints feel natural, 
                  not intrusive. The same channel used for "can I get more napkins" is the one that delivers 
                  your happy hour promotion and captures the guest's email for your loyalty program. It all 
                  works together, seamlessly, from a single tap.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Built on the Same Channel</h4>
                <ul>
                  <li>Welcome promotions delivered automatically on tap-in</li>
                  <li>Daily specials displayed before the guest's first message</li>
                  <li>VIP enrollment prompt embedded in the chat flow</li>
                  <li>Time-based offers appear based on when the guest sits down</li>
                  <li>All marketing feels organic — part of the conversation, not an ad</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="feature-stats">
        <div className="container">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-number">0</div>
              <p className="stat-label">Apps for guests to download — works instantly in the browser</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">&lt;5s</div>
              <p className="stat-label">From NFC tap to live chat — faster than raising a hand</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">24/7</div>
              <p className="stat-label">Daily operational reports track every request and response</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="feature-how-it-works">
        <div className="container">
          <h2>How Guest Messaging Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Guest Taps the NFC Tag</h4>
              <p>A small NFC tag at the table. Guest taps their phone. A web chat opens instantly — no download, no login, no friction.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>Guest Sends a Request</h4>
              <p>The guest types what they need in plain language. The AI assistant acknowledges, categorizes, and routes the request to your staff.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Staff Delivers</h4>
              <p>The request appears on your kiosk tablet with the table number. Staff tap to acknowledge, fulfill the request, and swipe to clear.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-cta">
        <div className="container">
          <h2>Give Every Guest a Voice at the Table</h2>
          <p>No more waiting, no more guessing, no more missed requests. Start connecting with your guests in real time.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
};

export default Messaging;
